import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ComponentProps } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'urql'
import { graphql } from '../gql'
import { CreateVendorInput } from '../gql/graphql'

interface IFormInput {
  name: string
  website: string
  commissionPercentage: number | string
  address: {
    street1: string
    street2?: string
    city: string
    province: string
    postalCode: string
    country: string
  }
}

const createVendorMutation = graphql(`
  mutation AddVendorDialogCreateVendor($input: CreateVendorInput!) {
    CreateVendor(input: $input) {
      name
      website
    }
  }
`)

export default function AddVendorDialog(props: {
  onClose: () => void
  dialogProps: ComponentProps<typeof Dialog>
}) {
  const [result, createVendor] = useMutation(createVendorMutation)

  const { fetching, error, data } = result

  const { handleSubmit, control, reset, setValue } = useForm<IFormInput>({
    defaultValues: {
      name: '',
      website: '',
      commissionPercentage: 2.5,
      address: {
        street1: '',
        street2: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
      },
    },
  })

  const handleCommissionBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const v = e.target.value
    if (typeof v !== 'string') return
    const n = Number.parseFloat(v.replace(/[^0-9.]/g, ''))
    if (!Number.isNaN(n)) {
      setValue('commissionPercentage', n)
    }
  }

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (fetching) return
    const commission =
      !data.commissionPercentage || data.commissionPercentage === '' ? undefined : data.commissionPercentage
    const input: CreateVendorInput = {
      ...data,
      commissionPercentage: commission as number,
    }
    await createVendor({ input }).then((r) => {
      if (!r.error) {
        reset()
        props.onClose()
      }
    })
  }

  return (
    <Dialog
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
      onClose={props.onClose}
      {...props.dialogProps}
    >
      <DialogTitle>Add a Vendor</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: true,
              maxLength: 255,
              minLength: { value: 3, message: 'Must be longer than 3 characters' },
            }}
            render={({ field, fieldState }) => (
              <TextField
                autoFocus
                required
                margin="dense"
                label="Name"
                fullWidth
                variant="standard"
                {...field}
                disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error && (fieldState.error?.message || 'Required')}
              />
            )}
          />
          <Controller
            name="website"
            control={control}
            rules={{ minLength: { value: 5, message: 'Must be longer than 5 characters' } }}
            render={({ field, fieldState }) => (
              <TextField
                autoFocus
                margin="dense"
                label="Website"
                fullWidth
                variant="standard"
                {...field}
                disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error && (fieldState.error?.message || 'Required')}
              />
            )}
          />
          <Controller
            name="commissionPercentage"
            control={control}
            rules={{
              validate: (v) => {
                if (!v) return undefined
                const n = Number.parseFloat(v as any as string)
                if (Number.isNaN(n)) {
                  return 'Must be a number'
                }
                return true
              },
              onBlur: handleCommissionBlur,
            }}
            render={({ field, fieldState }) => {
              return (
                <TextField
                  label="Commission (%) *"
                  {...field}
                  // disabled={fetching}
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    'Your normal commission percentage from this vendor. You can override this later on a per-order basis.'
                  }
                  slotProps={{
                    input: {
                      size: 'small',
                      sx: { flexGrow: 0, width: '10em', pr: 1 },
                      style: { textAlign: 'right' },
                      fullWidth: false,
                      endAdornment: '%',
                    },
                  }}
                />
              )
            }}
          />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Address
          </Typography>
          <Controller
            name="address.street1"
            control={control}
            rules={{ required: true, maxLength: 255, minLength: 5 }}
            render={({ field, fieldState }) => {
              return (
                <TextField
                  autoFocus
                  required
                  margin="dense"
                  label="Street 1"
                  fullWidth
                  variant="standard"
                  {...field}
                  disabled={fetching}
                  error={!!fieldState.error}
                  helperText={fieldState.error && (fieldState.error?.message || 'Required')}
                />
              )
            }}
          />
          <Controller
            name="address.street2"
            control={control}
            rules={{ maxLength: 255 }}
            render={({ field }) => (
              <TextField
                autoFocus
                margin="dense"
                label="Street 2"
                fullWidth
                variant="standard"
                {...field}
                disabled={fetching}
              />
            )}
          />
          <Controller
            name="address.city"
            control={control}
            rules={{ required: true, maxLength: 255 }}
            render={({ field, fieldState }) => (
              <TextField
                autoFocus
                required
                margin="dense"
                label="City"
                fullWidth
                variant="standard"
                {...field}
                disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error && (fieldState.error?.message || 'Required')}
              />
            )}
          />
          <Controller
            name="address.province"
            control={control}
            rules={{ required: true, maxLength: 255 }}
            render={({ field, fieldState }) => (
              <TextField
                autoFocus
                required
                margin="dense"
                label="Province"
                fullWidth
                variant="standard"
                {...field}
                disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error && (fieldState.error?.message || 'Required')}
              />
            )}
          />
          <Controller
            name="address.postalCode"
            control={control}
            rules={{ required: true, maxLength: 255 }}
            render={({ field, fieldState }) => (
              <TextField
                autoFocus
                required
                margin="dense"
                label="Postal Code"
                fullWidth
                variant="standard"
                {...field}
                disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error && (fieldState.error?.message || 'Required')}
              />
            )}
          />
          <Controller
            name="address.country"
            control={control}
            rules={{ required: true, maxLength: 255 }}
            render={({ field, fieldState }) => (
              <TextField
                autoFocus
                required
                margin="dense"
                label="Country"
                fullWidth
                variant="standard"
                {...field}
                disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error && (fieldState.error?.message || 'Required')}
              />
            )}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <p>An error has occurred:</p>
              {error.message}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button type="submit">Create</Button>
      </DialogActions>
    </Dialog>
  )
}
